import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import styled from 'styled-components';
import Meta from 'src/components/Meta';
import { PageContainer, Poppins } from 'src/common';
import { useGaEventPageView } from '../../utils/gaEvent';
import { Main, Logo, Image, RightContainer } from './comps/styles';

const Container = styled(PageContainer)`
  flex: 0.8;
  .auth-contents {
    max-width: 600px;
    margin: auto;
    display: flex;
    flex-direction: column;
    height: 100%;

    &__buttons {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      max-width: 400px;
    }

    &__footer {
      display: flex;
      margin-top: auto;
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    flex: 1;
  }
`;

const StyledLink = styled.a`
  color: rgb(158, 165, 183);
  &:visited {
    color: rgb(158, 165, 183);
  }
`;

const InlineLink = styled.a`
  color: inherit;
  font-weight: 600;
  &:visited {
    color: inherit;
  }
`;

const SIGNUP_VIDEO_URL = 'https://www.loom.com/share/1b1762f78766465cad52836d0df3c593';

const VerifyPage: React.FC = () => {
  const { loginWithRedirect } = useAuth0();

  useGaEventPageView('Cydea | Auth | Verify');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Meta title={`Cydea | Verify`} />
      <Main id="main">
        <Container $noSideMargin>
          <div className="auth-contents">
            <Logo />
            <Poppins px={56} weight={600}>
              Better risk analysis
            </Poppins>
            <Poppins css="margin-top: 42px" weight={600}>
              Thanks for registering! It’s time to verify your email address
            </Poppins>
            <Poppins css="margin-top: 21px" color="cflowerBlue">
              Check out our video that talks through the sign up process{' '}
              <InlineLink href={SIGNUP_VIDEO_URL} target="_blank" rel="noopener noreferrer">
                here
              </InlineLink>
              .
            </Poppins>
            <Poppins css="margin-top: 21px" color="cflowerBlue">
              Step 1: Done! Account Created: We have now created your Cydea Account.
            </Poppins>
            <Poppins css="margin-top: 21px" color="cflowerBlue">
              <strong>Step 2: (You are here!)</strong> Verify Your Account: Please check for an email from{' '}
              <InlineLink href="mailto:support@cydea.com">support@cydea.com</InlineLink> and click on{' '}
              <strong>“Verify Your Account”</strong>, or copy and paste the link into your browser.
            </Poppins>
            <Poppins css="margin-top: 21px" color="cflowerBlue">
              Step 3: Log in & Set Up MFA: Use your registered email address and the password you created to log in.
              Before you can access your workspace, you’ll need to set up MFA with an authenticator app.
            </Poppins>
            <Poppins css="margin-top: 21px" color="cflowerBlue">
              If you haven’t received or can’t find the email then please{' '}
              <span onClick={() => loginWithRedirect()} css="cursor: pointer">
                <strong>reset your password</strong>
              </span>
              .
            </Poppins>
            <Poppins css="margin-top: 21px" color="cflowerBlue">
              If you’re having trouble, please contact{' '}
              <InlineLink href="mailto:support@cydea.com">support@cydea.com</InlineLink>.
            </Poppins>

            <div className="auth-contents__footer">
              <Poppins px={14} color="cflowerBlue">
                © {new Date().getFullYear()} Cydea Ltd. Registered in England and Wales, 12204451. Cydea is a
                registered trademark. Read about your{' '}
                <StyledLink href="https://cydea.com/privacy/">privacy & how we use data</StyledLink>.
              </Poppins>
            </div>
          </div>
        </Container>
        <RightContainer>
          <Image />
        </RightContainer>
      </Main>
    </>
  );
};

export default VerifyPage;
